import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  PointerEvent as ReactPointerEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HoldfastPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Holdfast" (/holdfast) — an EXPIRATION paywall, built to the standing
// #paywalls directive: SECURITY & PRIVACY.
//
// The mechanic no other paywall here uses: PRESS-AND-HOLD to commit. Your
// Premium guard has gone SLACK, so the protective clasp around a mosaic of your
// memories sits loose and the glass reads soft/unfocused. You PRESS AND HOLD the
// central holdfast crest — and, as long as you hold, tension builds: the two
// jaws of the clasp draw shut around the vault core, the frosted glass pulls
// into focus, and the protection legend lights in a quick cascade. Reach full
// tension and it LATCHES with a settle — "HELD FAST." Let go before it latches
// and the tension honestly eases back to slack: you feel, in your thumb, that
// Premium is the thing that keeps the vault held tight without you having to.
//
// This is deliberately NOT a vertical drag rail (Everwatch), not a split-flap
// board (Solari), not a ring drawn closed (Halo), not an opening heirloom
// (Locket), not a dial / keypad / vault door / flip. The interaction identity is
// a HOLD-TO-COMMIT charge with an honest release-relaxes-back — commitment +
// progress-completion + contrast framing — and the register is tactile and
// grounding ("hold fast"), not cinematic or mechanical.
//
// Honest, App-Store compliant: loss aversion done HONESTLY — your photos never
// left and stayed private the whole time; the free vault is still private;
// holding fast only re-arms the EXTRA Premium guard. No countdown / scarcity, no
// fake urgency, explicit no-data-loss. Featured, most-prominent price is the
// YEARLY plan (7-day free trial, per-month broken out, honest Save %); monthly
// is a small secondary line. Reuses only the house font, colour tokens and the
// bundled byheart memory photos. Full light + dark, reduced-motion aware.
// Renders standalone on debug data; no native injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories held inside the clasp — reused bundled byheart photos, the only
// external assets this paywall pulls in. A 3×3 mosaic reads as "a vault of
// photos" at a glance.
const MOSAIC = [
  "m03",
  "m07",
  "m10",
  "m12",
  "m05",
  "m14",
  "m09",
  "m01",
  "m16",
].map(photo)

// The REAL Premium protections this holds tight. Order = cascade order when the
// clasp latches. `icon` is inline so nothing is fetched at runtime.
type Guard = {
  id: string
  label: string
  hint: string
  icon: JSX.Element
}

const GUARDS: Guard[] = [
  {
    id: "lock",
    label: "App lock",
    hint: "Face ID or PIN — only you get in",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7.8 10V7.7a4.2 4.2 0 0 1 8.4 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <rect
          x="5.4"
          y="10"
          width="13.2"
          height="9.2"
          rx="2.5"
          fill="currentColor"
        />
        <circle cx="12" cy="14.2" r="1.3" fill="var(--hf-core)" />
      </svg>
    ),
  },
  {
    id: "backup",
    label: "Encrypted backup",
    hint: "A safe, private copy kept for you",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M7 18a4 4 0 0 1-.6-7.96 5 5 0 0 1 9.7-1.2A3.8 3.8 0 0 1 18.5 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.4 13.6l1.9 1.9 3.5-3.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "breakin",
    label: "Break-in alerts",
    hint: "Snaps anyone who tries a wrong PIN",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M12 3.5l7 3v5.2c0 4.2-2.9 6.9-7 8.3-4.1-1.4-7-4.1-7-8.3V6.5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.4v4"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
        <circle cx="12" cy="15.2" r="1.05" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "decoy",
    label: "Decoy vault",
    hint: "A second passcode opens a stand-in",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <rect
          x="4.2"
          y="6.4"
          width="12"
          height="9"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <rect
          x="8.6"
          y="9.2"
          width="11.2"
          height="8.4"
          rx="2"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    id: "sync",
    label: "Private sync",
    hint: "Every device, end-to-end encrypted",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          d="M6 11.5a6 6 0 0 1 10.4-3.3M18 12.5a6 6 0 0 1-10.4 3.3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M16.6 5.4v3h-3M7.4 18.6v-3h3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "adfree",
    label: "Ad-free vault",
    hint: "Just your memories, nothing else",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <circle
          cx="12"
          cy="12"
          r="8.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M6.6 6.6l10.8 10.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

// How fast tension builds while held, and eases back when let go (ms to travel
// the full 0→1). Charge is a touch quicker than decay so a committed hold feels
// responsive while the release still reads as a gentle relaxation.
const CHARGE_MS = 900
const DECAY_MS = 1150

function HoldfastPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // level: the clasp tension, 0 (SLACK) → 1 (HELD FAST). Continuous.
  const [level, setLevel] = useState(0)
  const [latched, setLatched] = useState(false)
  const [pressing, setPressing] = useState(false)

  // Refs the rAF loop reads each frame (state is only for rendering).
  const levelRef = useRef(0)
  const latchedRef = useRef(false)
  const pressingRef = useRef(false)
  const autoRef = useRef(false) // the one-time load demo is "holding" for you
  const userTouched = useRef(false)
  const rafRef = useRef<number | null>(null)
  const lastTs = useRef<number | null>(null)
  const crestRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#0e131c" : "#eef1f7")
    actions.setFooterInsetColor(darkMode ? "#0f1420" : "#ffffff")
  }, [actions, darkMode])

  // One continuous animation loop for the whole life of the screen. It eases
  // `level` toward a target (1 while held / latched / auto-demoing, else 0) and
  // latches the clasp the moment a genuine hold reaches full.
  useEffect(() => {
    if (reduce) {
      levelRef.current = 1
      latchedRef.current = true
      setLevel(1)
      setLatched(true)
      return
    }

    const tick = (ts: number) => {
      if (lastTs.current == null) lastTs.current = ts
      const dt = Math.min(48, ts - lastTs.current)
      lastTs.current = ts

      const charging =
        pressingRef.current || autoRef.current || latchedRef.current
      let l = levelRef.current
      if (charging) l = Math.min(1, l + dt / CHARGE_MS)
      else l = Math.max(0, l - dt / DECAY_MS)

      if (
        l >= 1 &&
        !latchedRef.current &&
        (pressingRef.current || autoRef.current)
      ) {
        latchedRef.current = true
        autoRef.current = false
        setLatched(true)
        native.logMixpanelEvent("html_holdfast_latched", {
          via: userTouched.current ? "hold" : "auto",
        })
      }

      if (l !== levelRef.current) {
        levelRef.current = l
        setLevel(l)
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    // Kick off the one-time "held for you" demo after a beat so the payoff reads
    // within ~3s; any user touch cancels it and hands the clasp to the thumb.
    const startDemo = window.setTimeout(() => {
      if (!userTouched.current) autoRef.current = true
    }, 420)

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      window.clearTimeout(startDemo)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      lastTs.current = null
    }
  }, [reduce])

  const beginHold = useCallback(() => {
    userTouched.current = true
    autoRef.current = false
    if (latchedRef.current) return
    pressingRef.current = true
    setPressing(true)
  }, [])

  const endHold = useCallback(() => {
    pressingRef.current = false
    setPressing(false)
  }, [])

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* pointer capture unsupported */
    }
    beginHold()
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    endHold()
  }

  // Keyboard: Space/Enter arms the clasp to full (accessible equivalent of a
  // completed hold).
  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault()
      userTouched.current = true
      autoRef.current = false
      latchedRef.current = true
      levelRef.current = 1
      setLevel(1)
      setLatched(true)
    }
  }

  const held = latched
  const pct = Math.round(level * 100)
  // The clasp jaws swing from wide-open (slack) to shut (held).
  const jaw = 30 - level * 30 // degrees each jaw is rotated outward
  const slack = level <= 0.04 && !held

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_holdfast_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Hold my vault fast for a year"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""}`}
      style={{ ["--l" as any]: level }}
    >
      <div className={styles.aura} aria-hidden data-held={held} />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--hf-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker} data-held={held}>
            <i className={styles.kickDot} />
            {held
              ? "Held fast · Premium on watch"
              : "Premium guard has gone slack"}
          </span>
          <h1 className={styles.title}>Make your vault hold fast again.</h1>
          <p className={styles.sub}>
            Your photos never left and stayed private the whole time. Press and
            hold to draw the extra Premium guard back tight around them.
          </p>
        </header>
      </div>

      {/* ── The clasp stage: a mosaic of memories, held by the crest ─────── */}
      <div className={styles.stage}>
        <div className={styles.vault} data-held={held}>
          {/* The memories — always visible, never hidden. They pull into focus
              as the clasp draws tight. */}
          <div className={styles.mosaic} aria-hidden>
            {MOSAIC.map((src, i) => (
              <span
                key={i}
                className={styles.tile}
                style={{ backgroundImage: `url(${src})` }}
              />
            ))}
          </div>
          <div className={styles.frost} aria-hidden />
          <div className={styles.vignette} aria-hidden />

          {/* Status chip */}
          <div className={styles.statusChip} data-held={held}>
            <span className={styles.statusPip} />
            {held ? "HELD FAST" : slack ? "SLACK" : "DRAWING TIGHT"}
          </div>

          {/* ── The holdfast crest (press & hold) ───────────────────────── */}
          <button
            ref={crestRef}
            type="button"
            className={`${styles.crest} ${pressing ? styles.crestHold : ""} ${
              held ? styles.crestHeld : ""
            }`}
            style={{ ["--jaw" as any]: `${jaw}deg` }}
            aria-label="Press and hold to hold your vault fast"
            aria-pressed={held}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={endHold}
            onKeyDown={onKeyDown}
          >
            {/* tension halo — grows with the hold, no thin drawn ring */}
            <span className={styles.crestHalo} aria-hidden />

            {/* the two jaws that close around the core */}
            <span className={`${styles.jaw} ${styles.jawL}`} aria-hidden>
              <svg viewBox="0 0 60 120" width="46" height="92">
                <path
                  d="M50 8 C22 20 18 46 18 60 C18 74 22 100 50 112"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="7"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={`${styles.jaw} ${styles.jawR}`} aria-hidden>
              <svg viewBox="0 0 60 120" width="46" height="92">
                <path
                  d="M10 8 C38 20 42 46 42 60 C42 74 38 100 10 112"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="7"
                  strokeLinecap="round"
                />
              </svg>
            </span>

            {/* the vault core the jaws grip — a photo behind a lock */}
            <span className={styles.core} aria-hidden>
              <span
                className={styles.coreShot}
                style={{ backgroundImage: `url(${MOSAIC[4]})` }}
              />
              <span className={styles.coreGlass} />
              <svg
                className={styles.coreLock}
                viewBox="0 0 24 24"
                width="26"
                height="26"
              >
                <path
                  d="M8 11V8.4a4 4 0 0 1 8 0V11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5.6"
                  y="11"
                  width="12.8"
                  height="8.8"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
          </button>

          {/* tension readout */}
          <div className={styles.tension} aria-live="polite">
            <div className={styles.tensionBar}>
              <span
                className={styles.tensionFill}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={styles.tensionLabel}>
              {held
                ? "Every guard drawn tight"
                : slack
                  ? "Press & hold the crest"
                  : "Keep holding…"}
            </span>
          </div>

          {!userTouched.current && !reduce && !held && (
            <span className={styles.holdHint} aria-hidden>
              press &amp; hold
            </span>
          )}
        </div>

        {/* Protection legend — lights in a cascade as the clasp draws tight. */}
        <div className={styles.legend} data-held={held}>
          {GUARDS.map((g, i) => (
            <div
              key={g.id}
              className={`${styles.chip} ${level > 0.5 ? styles.chipOn : ""}`}
              style={{ ["--i" as any]: i }}
              title={g.hint}
            >
              <span className={styles.chipIcon}>{g.icon}</span>
              <span className={styles.chipName}>{g.label}</span>
              <span className={styles.chipLamp} aria-hidden />
            </div>
          ))}
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 18 }}>
        <p className={styles.reassure}>
          Your photos are always yours — nothing is ever deleted when a plan
          lapses. Holding fast only re-arms the extra Premium guard.
        </p>

        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase} data-held={held}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>

        <div className={styles.legalRow}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
        <p className={styles.fineprint}>
          Auto-renews until cancelled. Cancel anytime in Settings.
        </p>
      </div>
    </div>
  )
}

export default HoldfastPaywall
